'use client';
import {
    faCircleExclamation,
    faClose,
    faInfoCircle,
    faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';

interface ShowAlertProps {
    type: 'info' | 'warning' | 'error';
    text: string;
    show?: boolean;
    duration?: number;
    link?: string;
    onLinkClick?: () => void;
}

interface AlertContextType {
    showAlert: (data: ShowAlertProps) => void;
}

const AlertContext = createContext<AlertContextType | null>(null);

function AlertProvider({ children }: { children: ReactNode }) {
    const [alert, setAlert] = useState<ShowAlertProps>({
        type: 'warning',
        show: false,
        text: '',
        duration: 2000,
        link: '',
        onLinkClick: undefined,
    });

    const showAlert = useCallback(
        ({ type, text, duration = 2000 }: ShowAlertProps) => {
            if (!alert.show) {
                setAlert({ type, text, duration, show: true });
                setTimeout(() => setAlert({ type: 'info', text: '', show: false }), duration);
            }
        },
        [alert.show],
    );

    const color = useMemo(
        () =>
            alert.type === 'info'
                ? 'bg-blue-500  text-white'
                : alert.type === 'error'
                ? 'bg-red-500 text-white'
                : 'bg-yellow-500 text-white',
        [alert.type],
    );
    const icon = useMemo(
        () =>
            alert.type === 'info'
                ? faInfoCircle
                : alert.type === 'error'
                ? faCircleExclamation
                : faTriangleExclamation,

        [alert.type],
    );
    const closeIcon = useCallback(() => setAlert({ show: false, type: 'info', text: '' }), []);

    return (
        <AlertContext.Provider value={{ showAlert }}>
            {alert.show && (
                <div className={`fixed bottom-3 z-10 h-12 w-full  md:left-3  md:w-1/2`}>
                    <div className={`mx-3 flex h-12 items-center px-6  ${color} rounded-sm`}>
                        <FontAwesomeIcon icon={icon} className='flex-initial pr-2 text-white' />
                        <span className='flex-grow'>{alert.text}</span>
                        {!!alert.link && !!alert.onLinkClick && (
                            <span onClick={alert.onLinkClick}>{alert.link}</span>
                        )}
                        <FontAwesomeIcon
                            icon={faClose}
                            className=' pr-2 text-white hover:cursor-pointer'
                            onClick={closeIcon}
                        />
                    </div>
                </div>
            )}
            {children}
        </AlertContext.Provider>
    );
}

function useAlert() {
    return useContext(AlertContext) as AlertContextType;
}

export { useAlert, AlertProvider };
